import { cn } from "@/lib/utils";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "motion/react";
import { type ReactNode, useEffect } from "react";

/**
 * Fade-in + slide-up on mount with staggered children. Triggers on page load and tab switch.
 */
export function FadeInSection({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("stagger-fade-in", className)}>
      {children}
    </div>
  );
}

/**
 * Simple wrapper for staggered container layout.
 */
export function StaggerContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

/**
 * Simple wrapper for stagger item layout.
 */
export function StaggerItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

/**
 * Animated number counter that counts up from 0 to the target value.
 */
export function AnimatedCounter({
  value,
  formatFn,
  className,
  duration = 1.2,
}: {
  value: number;
  formatFn?: (n: number) => string;
  className?: string;
  duration?: number;
}) {
  const shouldReduce = useReducedMotion();
  const motionValue = useMotionValue(0);
  const display = useTransform(motionValue, (latest) => {
    const rounded = Math.round(latest);
    return formatFn ? formatFn(rounded) : rounded.toLocaleString();
  });

  useEffect(() => {
    if (shouldReduce) {
      motionValue.set(value);
      return;
    }

    const controls = animate(motionValue, value, {
      duration,
      ease: "easeOut",
    });

    return () => controls.stop();
  }, [value, duration, shouldReduce, motionValue]);

  return <motion.span className={cn(className)}>{display}</motion.span>;
}

/**
 * Thin shimmer bar positioned at the top of a relatively-positioned parent.
 */
export function CardShimmerBar() {
  return (
    <div className="absolute inset-x-0 top-0 z-10 h-1 overflow-hidden rounded-t-xl pointer-events-none">
      <div className="h-full w-full bg-gradient-to-r from-transparent via-primary/40 to-transparent animate-[bg-shimmer_2s_ease-in-out_infinite] bg-[length:200%_100%]" />
    </div>
  );
}

/**
 * Wrapper for cards that shows a shimmer loading bar while scraping.
 */
export function CardLoadingWrapper({
  children,
  isLoading,
  className,
}: {
  children: ReactNode;
  isLoading: boolean;
  className?: string;
}) {
  return (
    <div className={cn("relative overflow-hidden rounded-xl", className)}>
      {isLoading && <CardShimmerBar />}
      {children}
    </div>
  );
}

/**
 * Conditionally renders children.
 */
export function AnimatedCardPresence({
  children,
  isVisible,
  className,
}: {
  children: ReactNode;
  isVisible: boolean;
  className?: string;
}) {
  if (!isVisible) return null;
  return <div className={className}>{children}</div>;
}
